/**
 * Affiliate Dashboard — landing page for role=affiliate users.
 * Shows their commission rollup, recent commissions, list of introduced
 * customers, and their share link at the top.
 */
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Copy, LogOut, Users, Wallet, TrendingUp, Clock, MessageCircle, Send, Mail, Share2, QrCode, Download } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';

import API_URL from '@/config/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import ActivityTicker from '@/components/ActivityTicker';
import { MarketingKitCard } from '@/components/affiliate/MarketingKitCard';
import { InviteFriendCard } from '@/components/affiliate/InviteFriendCard';

const Stat = ({ icon: Icon, label, value, tone = 'gray' }) => (
  <Card className="p-4">
    <div className={`inline-flex items-center gap-2 text-xs uppercase tracking-wider text-${tone}-600`}>
      <Icon className="w-4 h-4" /> {label}
    </div>
    <p className="text-2xl font-bold mt-2 text-gray-900">{value}</p>
  </Card>
);

export default function AffiliateDashboard() {
  const [data, setData] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  // Credit balance + ledger — payouts settle as account credit rather
  // than a bank transfer, so the ambassador sees a running balance and
  // a full history right on their landing page.
  const [credit, setCredit] = useState({ balance: 0, ledger: [] });
  // Earnings analytics — range preset + optional brand filter. Presets
  // map 1:1 to the backend `range=` query on /affiliate/earnings.
  const [range, setRange] = useState('this_month');
  const [brandFilter, setBrandFilter] = useState(null); // null → all brands
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [earnings, setEarnings] = useState(null);
  const [earningsLoading, setEarningsLoading] = useState(false);

  // Ref to the QR canvas — declared here (before any early return) so
  // React's rules-of-hooks are satisfied. Points at the <div> that wraps
  // the <canvas> rendered by <QRCodeCanvas>.
  const qrCanvasRef = useRef(null);

  const load = async () => {
    setLoading(true);
    const [dRes, cRes, credRes] = await Promise.all([
      fetch(`${API_URL}/api/affiliate/dashboard`, { credentials: 'include' }),
      fetch(`${API_URL}/api/affiliate/customers`, { credentials: 'include' }),
      fetch(`${API_URL}/api/affiliate/credit-ledger`, { credentials: 'include' }),
    ]);
    if (dRes.ok) setData(await dRes.json());
    if (cRes.ok) setCustomers(await cRes.json());
    if (credRes.ok) setCredit(await credRes.json());
    setLoading(false);
  };

  const loadEarnings = async () => {
    setEarningsLoading(true);
    try {
      const params = new URLSearchParams();
      if (range === 'custom') {
        if (!customStart || !customEnd) return;
        params.set('start', customStart);
        params.set('end', customEnd);
      } else {
        params.set('range', range);
      }
      if (brandFilter) params.set('brand', brandFilter);
      const r = await fetch(`${API_URL}/api/affiliate/earnings?${params.toString()}`, { credentials: 'include' });
      if (r.ok) setEarnings(await r.json());
    } finally {
      setEarningsLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { loadEarnings(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [range, brandFilter, customStart, customEnd]);

  const logout = async () => {
    await fetch(`${API_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    window.location.href = '/login';
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading…</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-rose-600">Could not load your affiliate account.</div>;

  const { affiliate, recent_commissions } = data;
  const shareUrl = `${window.location.origin}/join/${affiliate.slug}`;

  // Single pre-written share message. Kept warm + short so it reads well
  // as an inline WhatsApp/SMS message without feeling salesy. `\n\n` renders
  // properly on WhatsApp / most SMS clients / Gmail.
  const shareMessage = `Sign up to Ox Resin through my link and you'll get the best trade rates on every order — patios, drives, the lot.\n\n${shareUrl}`;
  const shareSubject = 'Your best Ox Resin rates';

  const copy = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success('Link copied — share it anywhere');
  };
  const copyMessage = () => {
    navigator.clipboard.writeText(shareMessage);
    toast.success('Message copied — paste it into any chat');
  };
  const downloadQr = () => {
    const canvas = qrCanvasRef.current?.querySelector('canvas');
    if (!canvas) { toast.error('QR code not ready yet — try again in a moment.'); return; }
    const link = document.createElement('a');
    link.download = `ox-resin-referral-${affiliate.slug}.png`;
    link.href = canvas.toDataURL('image/png');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('QR code downloaded — pop it on a flyer, van, or business card.');
  };

  // Build the deep-link URLs. Native share sheet on mobile (Web Share API)
  // is preferred where available since it gives access to every installed
  // messaging app — WhatsApp / Telegram / Signal / Messages / Instagram DM.
  // Fall back to explicit WhatsApp / SMS / Email links on desktop.
  const openNativeShare = async () => {
    if (!navigator.share) return copyMessage();
    try {
      await navigator.share({ title: shareSubject, text: shareMessage, url: shareUrl });
    } catch {/* user cancelled — no-op */}
  };
  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;
  const smsHref      = `sms:?&body=${encodeURIComponent(shareMessage)}`;
  const mailHref     = `mailto:?subject=${encodeURIComponent(shareSubject)}&body=${encodeURIComponent(shareMessage)}`;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b px-4 py-3 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900" data-testid="affiliate-header-name">{affiliate.name}</h1>
          <p className="text-xs text-gray-500">Ox Ambassador portal · {affiliate.commission_percent}% commission</p>
        </div>
        <Button variant="outline" onClick={logout}><LogOut className="w-4 h-4 mr-1" /> Log out</Button>
      </header>

      <main className="max-w-5xl mx-auto p-4 space-y-6">
        {/* Share link */}
        <Card className="p-4 bg-gradient-to-r from-[#7A0B7A]/10 to-purple-100 border-[#7A0B7A]/20">
          <div className="flex flex-col md:flex-row md:items-start gap-4">
            {/* Link + share buttons — takes the remaining width */}
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-wider text-[#7A0B7A] font-bold">Your unique link</p>
              <div className="flex items-center gap-2 mt-2">
                <code className="flex-1 bg-white/70 rounded-lg px-3 py-2 text-sm truncate" data-testid="share-url">{shareUrl}</code>
                <Button onClick={copy} className="bg-[#7A0B7A] hover:bg-[#5e0860] text-white" data-testid="share-copy"><Copy className="w-4 h-4 mr-1" /> Copy</Button>
              </div>
              <p className="text-xs text-gray-600 mt-2">Everyone who signs up through this link is permanently linked to you — you earn {affiliate.commission_percent}% on every paid order they place.</p>

              {/* One-tap share buttons. Uses the native share sheet on mobile
                  (gives access to every installed messaging app in one tap) and
                  falls back to explicit deep-links on desktop. */}
              <div className="mt-4 pt-4 border-t border-[#7A0B7A]/15">
                <p className="text-xs uppercase tracking-wider text-[#7A0B7A] font-bold mb-2">Share in one tap</p>
                <div className="flex flex-wrap gap-2">
                  <a href={whatsappHref} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp">
                    <Button className="bg-[#25D366] hover:bg-[#1ea855] text-white">
                      <MessageCircle className="w-4 h-4 mr-1.5" /> WhatsApp
                    </Button>
                  </a>
                  <Button
                    onClick={openNativeShare}
                    variant="outline"
                    className="bg-white hover:bg-purple-50 border-[#7A0B7A]/30 text-[#7A0B7A]"
                    data-testid="share-native"
                  >
                    <Share2 className="w-4 h-4 mr-1.5" /> More apps
                  </Button>
                  <a href={smsHref} data-testid="share-sms">
                    <Button className="bg-slate-700 hover:bg-slate-800 text-white">
                      <Send className="w-4 h-4 mr-1.5" /> SMS
                    </Button>
                  </a>
                  <a href={mailHref} data-testid="share-email">
                    <Button variant="outline" className="bg-white">
                      <Mail className="w-4 h-4 mr-1.5" /> Email
                    </Button>
                  </a>
                  <Button
                    onClick={copyMessage}
                    variant="outline"
                    className="bg-white"
                    data-testid="share-copy-message"
                  >
                    <Copy className="w-4 h-4 mr-1.5" /> Copy message
                  </Button>
                </div>
                <p className="text-[11px] text-gray-500 mt-2 italic">
                  Pre-written message: "Sign up to Ox Resin through my link and you'll get the best trade rates on every order — patios, drives, the lot."
                </p>
              </div>
            </div>

            {/* QR card — scan on a phone to visit the referral link.
                Perfect for van signage, print flyers, and business cards.
                A fixed white "quiet zone" gives every scanner enough
                contrast even on the gradient background. */}
            <div
              className="flex flex-col items-center gap-2 shrink-0 bg-white rounded-xl border border-[#7A0B7A]/25 p-3"
              data-testid="share-qr-card"
            >
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#7A0B7A] font-bold">
                <QrCode className="w-3 h-3" /> Scan me
              </div>
              <div ref={qrCanvasRef} className="p-1 bg-white">
                <QRCodeCanvas
                  value={shareUrl}
                  size={144}
                  level="M"
                  bgColor="#ffffff"
                  fgColor="#7A0B7A"
                  includeMargin={false}
                  data-testid="share-qr-canvas"
                />
              </div>
              <Button
                onClick={downloadQr}
                variant="outline"
                size="sm"
                className="text-[11px] w-full bg-white border-[#7A0B7A]/30 text-[#7A0B7A] hover:bg-purple-50"
                data-testid="share-qr-download"
              >
                <Download className="w-3.5 h-3.5 mr-1" /> Download PNG
              </Button>
              <p className="text-[10px] text-gray-500 text-center max-w-[160px] leading-tight">
                Print on flyers, van signs, business cards
              </p>
            </div>
          </div>
        </Card>

        {/* Marketing kit + send-to-a-mate */}
        <MarketingKitCard affiliate={affiliate} shareUrl={shareUrl} />
        <InviteFriendCard />

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat icon={Users} label="Customers" value={affiliate.stats.customers_count} tone="blue" />
          <Stat icon={TrendingUp} label="Gross sales" value={`£${(affiliate.stats.gross_sales || 0).toFixed(2)}`} tone="purple" />
          <Stat icon={Clock} label="Pending" value={`£${(affiliate.stats.commission_pending || 0).toFixed(2)}`} tone="amber" />
          <Stat icon={Wallet} label="Paid" value={`£${(affiliate.stats.commission_paid || 0).toFixed(2)}`} tone="emerald" />
        </div>

        {/* Live activity ticker */}
        <ActivityTicker />

        {/* Account credit — Ox pays commissions as credit on the
            ambassador's account (not a bank transfer). This card is
            deliberately front-and-centre so the ambassador knows what's
            available to spend + how each movement was made. */}
        <Card className="p-4 border-2 border-emerald-200 bg-emerald-50/40" data-testid="affiliate-credit-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Ox Account Credit</p>
              <p className="text-4xl font-bold text-emerald-800 mt-1" data-testid="credit-balance">£{credit.balance.toFixed(2)}</p>
              <p className="text-[11px] text-emerald-800/70 mt-1">Ready to spend against your next Ox material invoice — chat to Ox to redeem.</p>
            </div>
          </div>
          {credit.ledger.length === 0 ? (
            <p className="text-xs text-gray-500 py-3 text-center">No credit movements yet.</p>
          ) : (
            <div className="border-t border-emerald-200/70 pt-2 max-h-64 overflow-y-auto">
              <p className="text-[10px] uppercase tracking-wider text-emerald-800 font-bold mb-1">Ledger</p>
              <div className="divide-y divide-emerald-100">
                {credit.ledger.map((row) => {
                  const positive = row.amount >= 0;
                  const kindLabel = { earned: 'Earned', redeemed: 'Redeemed', adjustment: 'Adjustment' }[row.type] || row.type;
                  return (
                    <div key={row.id} className="flex items-center gap-3 py-2 text-sm" data-testid={`ledger-row-${row.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900">{kindLabel}{row.notes ? ` — ${row.notes}` : ''}</p>
                        <p className="text-[11px] text-gray-500">{(row.created_at || '').slice(0, 16).replace('T', ' ')}</p>
                      </div>
                      <div className={`w-24 text-right font-bold ${positive ? 'text-emerald-700' : 'text-rose-700'}`}>
                        {positive ? '+' : '−'}£{Math.abs(row.amount).toFixed(2)}
                      </div>
                      <div className="w-20 text-right text-[11px] text-gray-500">£{(row.running_balance || 0).toFixed(2)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Card>

        {/* Commission analytics — range presets + brand filter chips
            drive the /affiliate/earnings query. `earned` is
            (pending + approved + paid) so it matches what the
            ambassador will invoice for. */}
        <Card className="p-4" data-testid="affiliate-analytics">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="font-bold text-gray-900">Commission analytics</h2>
            {earnings?.range && (
              <span className="text-[11px] text-gray-500">
                {earnings.range.start} → {earnings.range.end} · {earnings.range.brand ? `brand: ${earnings.range.brand}` : 'all brands'}
              </span>
            )}
          </div>

          {/* Range preset chips */}
          <div className="flex flex-wrap items-center gap-1.5 mb-2" role="tablist" aria-label="Time range">
            {[
              { k: 'today', l: 'Today' },
              { k: 'this_week', l: 'This week' },
              { k: 'this_month', l: 'This month' },
              { k: 'last_month', l: 'Last month' },
              { k: 'last_7d', l: 'Last 7d' },
              { k: 'last_30d', l: 'Last 30d' },
              { k: 'year_to_date', l: 'Year to date' },
              { k: 'all_time', l: 'All time' },
              { k: 'custom', l: 'Custom…' },
            ].map((r) => {
              const active = range === r.k;
              return (
                <button
                  key={r.k}
                  onClick={() => setRange(r.k)}
                  data-testid={`range-${r.k}`}
                  className={[
                    'px-3 py-1 rounded-full text-xs font-semibold border transition-colors',
                    active
                      ? 'bg-[#7A0B7A] text-white border-[#7A0B7A]'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50',
                  ].join(' ')}
                >{r.l}</button>
              );
            })}
          </div>

          {/* Custom date range inputs — only shown when Custom is picked */}
          {range === 'custom' && (
            <div className="flex flex-wrap items-center gap-2 mb-3 text-sm">
              <input
                type="date" value={customStart} max={customEnd || undefined}
                onChange={(e) => setCustomStart(e.target.value)}
                data-testid="custom-start"
                className="border rounded-md px-2 py-1 bg-white"
              />
              <span className="text-gray-400">→</span>
              <input
                type="date" value={customEnd} min={customStart || undefined}
                onChange={(e) => setCustomEnd(e.target.value)}
                data-testid="custom-end"
                className="border rounded-md px-2 py-1 bg-white"
              />
              {(!customStart || !customEnd) && (
                <span className="text-[11px] text-gray-500">Pick both dates to load the range.</span>
              )}
            </div>
          )}

          {/* Brand filter chips */}
          <div className="flex flex-wrap items-center gap-1.5 mb-4" role="tablist" aria-label="Brand filter">
            {[
              { k: null, l: 'All brands', color: 'bg-gray-100 text-gray-700 border-gray-300' },
              { k: 'probound', l: 'Bound', color: 'bg-[#EBF3FB] text-[#2F5E96] border-[#2F5E96]' },
              { k: 'proepoxy', l: 'Epoxy', color: 'bg-[#F5EAF5] text-[#7A0B7A] border-[#7A0B7A]' },
              { k: 'prodecor', l: 'Decor', color: 'bg-[#FBF3E0] text-[#D5AB09] border-[#D5AB09]' },
            ].map((b) => {
              const active = brandFilter === b.k;
              return (
                <button
                  key={b.l}
                  onClick={() => setBrandFilter(b.k)}
                  data-testid={`brand-filter-${b.k || 'all'}`}
                  className={[
                    'px-3 py-1 rounded-full text-xs font-semibold border-2 transition-all',
                    active ? `${b.color} shadow-sm` : 'bg-white text-gray-400 border-gray-200 hover:bg-gray-50',
                  ].join(' ')}
                >{b.l}</button>
              );
            })}
          </div>

          {earningsLoading || !earnings ? (
            <p className="text-sm text-gray-500 py-6 text-center">Loading…</p>
          ) : earnings.totals.orders_count === 0 ? (
            <p className="text-sm text-gray-500 py-8 text-center">
              No commission activity for this filter. Try a wider range or clear the brand filter.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="rounded-lg border p-3">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Orders</p>
                  <p className="text-xl font-bold text-gray-900 mt-1" data-testid="earnings-orders">{earnings.totals.orders_count}</p>
                </div>
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Earned</p>
                  <p className="text-xl font-bold text-emerald-800 mt-1" data-testid="earnings-earned">£{earnings.totals.earned.toFixed(2)}</p>
                </div>
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">Pending</p>
                  <p className="text-xl font-bold text-amber-800 mt-1">£{earnings.totals.pending.toFixed(2)}</p>
                </div>
                <div className="rounded-lg border border-emerald-300 bg-white p-3">
                  <p className="text-[10px] uppercase tracking-wider text-emerald-800 font-bold">Paid out</p>
                  <p className="text-xl font-bold text-emerald-900 mt-1">£{earnings.totals.paid.toFixed(2)}</p>
                </div>
              </div>

              {/* Per-brand breakdown — only render brands with activity so
                  a Bound-only ambassador doesn't see empty Epoxy/Decor rows.
                  Hidden entirely when a specific brand filter is active
                  (that filter already answers "which brand"). */}
              {!brandFilter && Object.keys(earnings.by_brand).length > 1 && (
                <div className="space-y-2 mb-4">
                  {['probound', 'proepoxy', 'prodecor', 'other'].map((b) => {
                    const stats = earnings.by_brand[b];
                    if (!stats) return null;
                    const meta = {
                      probound: { label: 'Bound', color: 'text-[#2F5E96]' },
                      proepoxy: { label: 'Epoxy', color: 'text-[#7A0B7A]' },
                      prodecor: { label: 'Decor', color: 'text-[#D5AB09]' },
                      other:    { label: 'Other', color: 'text-gray-500' },
                    }[b];
                    return (
                      <div key={b} className="flex items-center justify-between border rounded-md px-3 py-2" data-testid={`earnings-brand-${b}`}>
                        <div className={`font-bold text-sm ${meta.color}`}>{meta.label}</div>
                        <div className="text-xs text-gray-500 flex-1 text-right mr-4">
                          {stats.orders} order{stats.orders === 1 ? '' : 's'} · £{stats.gross.toFixed(2)} gross
                        </div>
                        <div className="text-sm font-bold text-emerald-700">£{stats.commission.toFixed(2)}</div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Row-by-row list — customer first name only for privacy,
                  status pill on the right so pending vs paid is visible
                  at a glance. */}
              <div className="border-t pt-3">
                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">
                  {earnings.rows.length} commission{earnings.rows.length === 1 ? '' : 's'} in this range
                </p>
                <div className="divide-y">
                  {earnings.rows.map((row) => {
                    const brand = row.brand || 'other';
                    const brandMeta = {
                      probound: { label: 'Bound', color: 'text-[#2F5E96]' },
                      proepoxy: { label: 'Epoxy', color: 'text-[#7A0B7A]' },
                      prodecor: { label: 'Decor', color: 'text-[#D5AB09]' },
                    }[brand] || { label: 'Other', color: 'text-gray-500' };
                    const statusPill = {
                      pending: 'bg-amber-100 text-amber-800',
                      approved: 'bg-emerald-100 text-emerald-800',
                      paid: 'bg-emerald-600 text-white',
                      cancelled: 'bg-rose-100 text-rose-800 line-through',
                    }[row.status] || 'bg-gray-100 text-gray-700';
                    return (
                      <div key={row.id} className="flex items-center gap-3 py-2 text-sm" data-testid={`commission-row-${row.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 truncate">{row.customer_first_name || 'Customer'}</p>
                          <p className="text-[11px] text-gray-500">
                            <span className={`font-bold ${brandMeta.color}`}>{brandMeta.label}</span>
                            {' · '}£{(row.order_value || 0).toFixed(2)} @ {(row.commission_rate || 0).toFixed(1)}%
                            {' · '}{(row.order_date || '').slice(0, 10)}
                          </p>
                        </div>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${statusPill}`}>{row.status}</span>
                        <div className="w-20 text-right font-bold text-emerald-700">£{(row.commission_amount || 0).toFixed(2)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </Card>

        {/* Recent commissions */}
        <Card className="p-4">
          <h2 className="font-bold text-gray-900 mb-3">Recent commissions</h2>
          {recent_commissions.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">No orders yet. Once a customer of yours places a paid order, your commission will appear here.</p>
          ) : (
            <div className="space-y-2">
              {recent_commissions.map((r) => (
                <div key={r.id} className="flex items-center gap-3 border rounded-lg p-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold">{r.order_type} · {r.order_id}</p>
                    <p className="text-xs text-gray-500">£{(r.order_value || 0).toFixed(2)} order · {r.commission_rate}% = <span className="font-semibold text-emerald-700">£{(r.commission_amount || 0).toFixed(2)}</span></p>
                  </div>
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                    r.status === 'paid' ? 'bg-emerald-100 text-emerald-800' :
                    r.status === 'approved' ? 'bg-amber-100 text-amber-800' :
                    r.status === 'pending' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-200 text-gray-600'
                  }`}>{r.status}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Customers */}
        <Card className="p-4">
          <h2 className="font-bold text-gray-900 mb-3">Your customers <span className="text-sm font-normal text-gray-500">({customers.length})</span></h2>
          {customers.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">No customers yet — share your unique link to start growing your network.</p>
          ) : (
            <div className="divide-y">
              {customers.map((c) => (
                <div key={c.user_id} className="py-2 text-sm flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">{c.name || c.email}</p>
                    <p className="text-xs text-gray-500">{c.email}{c.company_name ? ` · ${c.company_name}` : ''}</p>
                  </div>
                  <p className="text-xs text-gray-400">joined {new Date(c.created_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          )}
        </Card>
      </main>
    </div>
  );
}
